import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Play, Pencil, Trash2, BookOpen, ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

interface Exercise {
  id: number;
  title: string;
  type: 'word_selection' | 'gender_selection';
  created_at: string;
  stimuli_count: number;
}

const TYPE_LABELS: Record<string, string> = {
  word_selection: 'Selección de palabra',
  gender_selection: 'Género (EL / LA)',
};

export default function HerramientasLibraryPage() {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const res = await api.get('/rehab/exercises');
      if (res.data.ok) setExercises(res.data.data);
    } catch {
      toast.error('Error al cargar los ejercicios');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, title: string) => {
    if (!confirm(`¿Eliminar el ejercicio "${title}"?`)) return;
    try {
      await api.delete(`/rehab/exercises/${id}`);
      toast.success('Ejercicio eliminado');
      setExercises(prev => prev.filter(e => e.id !== id));
    } catch {
      toast.error('Error al eliminar el ejercicio');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link
            to="/profesional"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Panel profesional
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-display font-bold mb-2">Herramientas</h1>
              <p className="text-white/70">Ejercicios de logopedia y rehabilitación</p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/herramientas/fichas"
                className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white/70 hover:text-white hover:border-white/40 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Fichas PDF
              </Link>
              <button
                onClick={() => navigate('/herramientas/crear')}
                className="flex items-center gap-2 px-4 py-2 bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Nuevo ejercicio
              </button>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
            <p className="mt-4 text-white/70">Cargando ejercicios...</p>
          </div>
        ) : exercises.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-container text-center py-16"
          >
            <BookOpen className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Sin ejercicios todavía</h2>
            <p className="text-white/60 mb-6">Crea tu primer ejercicio para empezar</p>
            <button
              onClick={() => navigate('/herramientas/crear')}
              className="px-6 py-3 bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
            >
              Crear ejercicio
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {exercises.map((exercise, index) => (
              <motion.div
                key={exercise.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-container flex items-center justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg truncate">{exercise.title}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300">
                      {TYPE_LABELS[exercise.type]}
                    </span>
                    <span className="text-sm text-white/50">
                      {exercise.stimuli_count} estímulo{exercise.stimuli_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => navigate(`/herramientas/sesion/${exercise.id}`)}
                    className="p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors"
                    title="Iniciar sesión"
                  >
                    <Play className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => navigate(`/herramientas/editar/${exercise.id}`)}
                    className="p-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition-colors"
                    title="Editar"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(exercise.id, exercise.title)}
                    className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
